import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { HEADER_SPACING_PRESETS, NoSidebarPageLayout } from '@insurancemenu/imenu-components-lib';
import { SUBSCRIBER_CARD_DATA } from 'global/checklists-helpers/SubscriberChecklistHelper';
import useSubscriberTaskUrlParams from 'global/hooks/useSubscriberTaskUrlParams';
import SubscriberDependentsPageStore, {
  SubscriberDependentsPageStoreContext,
  useSubscriberDependentsPageStore,
} from 'store/pages/subscriber/subscriber-dependents/pageStore';
import { Add } from '@insurancemenu/imenu-components-lib/dist/icons';
import { colors } from '@insurancemenu/imenu-components-lib/dist/utils';
import SubscriberDependentsTable from 'components/common/election/SubscriberDependentsTable';
import { SUBSCRIBER_DEPENDENTS_PAGE_TEST_IDS } from 'components/subscriber/constants';
import { buildGroupContextMoreInfoBlobProps } from 'components/common/group/GroupContextMoreInfoBlob';
import { buildGroupContactInfoMap } from 'global/services-helpers/groupService';
import { AddDependentButtonStyled, DynamicFormModalStyled } from './styles';

export const PageContent: React.FC = observer(() => {
  const {
    addDependentModalStore,
    isLoading,
    dependentDelegates,
    subscriberBasicInfo,
    formId,
    group,
    getInitialData,
    returnBack,
    completeChecklistStep,
  } = useSubscriberDependentsPageStore();

  useEffect(() => {
    void getInitialData();
  }, []);

  return (
    <NoSidebarPageLayout
      contentWidth="standard"
      isInitialLoading={isLoading}
      isInfoSectionLoading={isLoading}
      moreInfoBlob={buildGroupContextMoreInfoBlobProps({
        blobTitle: group?.name || '',
        generalInfoMap: { group: group?.name || '' },
        contactInfoMap: buildGroupContactInfoMap(group?.contactInfo ?? null),
      })}
      pageHeader={{
        title: SUBSCRIBER_CARD_DATA.SUBSCRIBER_DEPENDENTS.title,
        subtitle: SUBSCRIBER_CARD_DATA.SUBSCRIBER_DEPENDENTS.text,
        spacing: HEADER_SPACING_PRESETS.withTopSection,
        onBackButtonClick: returnBack,
      }}
      pageFooter={{
        onBack: returnBack,
        onComplete: completeChecklistStep,
        completeButtonText: 'Complete',
        disableCompleteButton: isLoading,
      }}
    >
      <AddDependentButtonStyled
        size="large"
        color="custom"
        customColor={colors.green[600]}
        leftIconProp={<Add />}
        onClick={addDependentModalStore?.openAddDependentModal}
        data-test-id={SUBSCRIBER_DEPENDENTS_PAGE_TEST_IDS.ADD_DEPENDENT_BUTTON}
      >
        Add Dependent
      </AddDependentButtonStyled>
      {addDependentModalStore && (
        <DynamicFormModalStyled
          show={addDependentModalStore.showAddDependentModal}
          onClose={addDependentModalStore.closeAddDependentModal}
          formDelegate={addDependentModalStore.dynamicFormDelegate}
          completeButtonText="Save"
        />
      )}
      <SubscriberDependentsTable
        isLoading={isLoading}
        dependentDelegates={dependentDelegates}
        subscriberBasicInfo={subscriberBasicInfo}
        formId={formId}
      />
    </NoSidebarPageLayout>
  );
});

const SubscriberDependentsPage: React.FC = () => {
  const { groupId, subscriberId, entityId } = useSubscriberTaskUrlParams();

  const pageStore = useMemo(
    () =>
      new SubscriberDependentsPageStore({
        groupId,
        subscriberId,
        entityId,
      }),
    [],
  );
  return (
    <SubscriberDependentsPageStoreContext.Provider value={pageStore}>
      <PageContent />
    </SubscriberDependentsPageStoreContext.Provider>
  );
};

export default SubscriberDependentsPage;
